import HexCoords from '../grid/HexCoords';
import HexPosition from '../components/HexPosition';
import type { Entity } from './entityManager';

// This is a forward declaration of the interface that ProjectedOccupancy expects.
// The full TickResolutionContext class will be implemented in a later step.
export interface TickResolutionContext {
  isActionForEntityResolved(entityId: number): boolean;
}

export default class ProjectedOccupancy {
  private occupancy: Map<string, Entity> = new Map();

  constructor() {}

  /**
   * Populates the initial state from a list of entities with HexPosition.
   * @param entities A list of entities to place on the board.
   */
  public setInitialOccupancy(entities: Entity[]): void {
    this.occupancy.clear();
    for (const entity of entities) {
      const hexPosition = entity.getComponent(HexPosition);
      if (hexPosition) {
        if (this.occupancy.has(hexPosition.coords.key)) {
            throw new Error(`Duplicate entity at ${hexPosition.coords.key} during initial occupancy setting.`);
        }
        this.occupancy.set(hexPosition.coords.key, entity);
      }
    }
  }

  /**
   * Retrieves the entity at a given set of coordinates.
   * @param coords The hex coordinates to check.
   * @returns The entity at the location, or undefined if the hex is empty.
   */
  public getOccupant(coords: HexCoords): Entity | undefined {
    return this.occupancy.get(coords.key);
  }

  public getAllOccupants(): Entity[] {
    return Array.from(this.occupancy.values());
  }

  public getAllEntries(): [string, Entity][] {
    return Array.from(this.occupancy.entries());
  }

  /**
   * Finds the coordinates of a given entity.
   * @param entity The entity to find.
   * @returns The HexCoords of the entity, or null if not found.
   */
  public findEntity(entity: Entity): HexCoords | null {
    for (const [key, occupant] of this.occupancy.entries()) {
      if (occupant.id === entity.id) {
        return HexCoords.get(key);
      }
    }
    return null;
  }

  /**
   * Attempts to move an entity from one location to another, enforcing invariants.
   * Will throw an error if the move is illegal (e.g., into a hex occupied by a resolved actor).
   * @param entity The entity to move.
   * @param fromCoords The entity's current location.
   * @param toCoords The entity's desired new location.
   * @param context The resolution context, used for checking resolved actions.
   * @param options.allowOverwrite If true, allows moving into an occupied hex. Used for conflict winners.
   */
  public moveEntity(entity: Entity, fromCoords: HexCoords, toCoords: HexCoords, context: TickResolutionContext, options: { allowOverwrite?: boolean } = {}): void {
    if (this.getOccupant(fromCoords)?.id !== entity.id) {
      throw new Error(`Entity ${entity.id} is not at fromCoords ${fromCoords.key}.`);
    }

    const occupant = this.getOccupant(toCoords);
    if (occupant && occupant.id !== entity.id && !options.allowOverwrite) {
      if (context.isActionForEntityResolved(occupant.id)) {
        throw new Error(`Cannot move into ${toCoords.key}, hex is occupied by entity ${occupant.id} whose action is resolved.`);
      }
      throw new Error(`Cannot move into ${toCoords.key}, hex is occupied by entity ${occupant.id}.`);
    }

    this.occupancy.delete(fromCoords.key);
    this.occupancy.set(toCoords.key, entity);
  }
}
